import React from "react";
import { Col, Row, Typography, Collapse, Divider, Alert, Tag } from "antd";
import { QuestionCircleOutlined, DollarOutlined, CheckCircleOutlined } from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

const Payment = () => {
  return (
    <Row type="flex">
      <Col span={20} offset={1}>
        <Title level={2}>Payment Guide</Title>
        <Paragraph style={{ fontSize: '16px', marginBottom: '24px' }}>
          This guide covers everything you need to know about payment processing, pricing, 
          and payment-related issues for the Pre-Qualification Questionnaire (PQQ) process.
        </Paragraph>

        <Alert
          message="Payment Required"
          description="Your PQQ submission will remain pending until payment is received and processed. 
          Payment must be completed before you can certify and submit your PQQ."
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <Divider />

        <Title level={4}>Pricing Information</Title>
        <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f0f2f5', borderRadius: '4px' }}>
          <Paragraph style={{ marginBottom: '8px' }}>
            <DollarOutlined style={{ marginRight: '8px', fontSize: '18px' }} />
            <Text strong>Initial PQQ Processing Fee:</Text> <Tag color="blue" style={{ fontSize: '16px', padding: '4px 12px' }}>$1,250.00</Tag>
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            <Text strong>Annual Recertification Fee:</Text> <Tag color="green" style={{ fontSize: '16px', padding: '4px 12px' }}>$750.00 + 2.9% transaction fee</Tag>
          </Paragraph>
        </div>

        <Divider />

        <Title level={4}>Frequently Asked Questions</Title>

        <Collapse defaultActiveKey={['1']} ghost>
          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                How much does the PQQ process cost?
              </span>
            } 
            key="1"
          >
            <Paragraph>
              The initial Pre-Qualification Questionnaire processing fee is <Text strong>$1,250.00</Text>. 
              This one-time fee covers the initial pre-qualification process and evaluation.
            </Paragraph>
            <Paragraph>
              For annual recertification, there is an additional fee of <Text strong>$750.00 plus a 2.9% 
              transaction fee</Text> (approximately $771.75 total). This recertification fee is required 
              to maintain your pre-qualified status on an annual basis.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                When do I need to make payment?
              </span>
            } 
            key="2"
          >
            <Paragraph>
              Payment is required after you complete your i4 Vendor Profile (100% completion) and before 
              you can certify and submit your PQQ. The payment process should be initiated from your 
              Dashboard or the Payment section.
            </Paragraph>
            <Paragraph>
              <Text strong>Important:</Text> Your PQQ submission will remain in "Pending" status until 
              payment is received and processed by i4 Strategies.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                What payment methods are accepted?
              </span>
            } 
            key="3"
          >
            <Paragraph>
              Payments are processed securely through our payment gateway, which accepts:
            </Paragraph>
            <ul>
              <li>Major credit cards (Visa, MasterCard, American Express, Discover)</li>
              <li>Debit cards</li>
            </ul>
            <Paragraph>
              All payments are processed securely using industry-standard encryption. Your payment 
              information is never stored on our servers.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                How long does payment processing take?
              </span>
            } 
            key="4"
          >
            <Paragraph>
              Most payments are processed immediately. Once your payment is successfully processed, 
              you should be able to proceed with certifying and submitting your PQQ within a few minutes.
            </Paragraph>
            <Paragraph>
              In some cases, payment verification may take up to 24-48 hours. If your payment status 
              doesn't update after 48 hours, please contact support.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                What happens if my payment fails?
              </span>
            } 
            key="5"
          >
            <Paragraph>
              If your payment fails, you'll receive an error message. Common reasons for payment 
              failures include:
            </Paragraph>
            <ul>
              <li>Insufficient funds</li>
              <li>Card expiration or incorrect card information</li>
              <li>Bank security restrictions</li>
              <li>Network connectivity issues</li>
            </ul>
            <Paragraph>
              <Text strong>What to do:</Text>
            </Paragraph>
            <ol>
              <li>Verify your card information is correct</li>
              <li>Contact your bank to ensure the transaction isn't being blocked</li>
              <li>Try using a different payment method</li>
              <li>If problems persist, contact support at <Text strong>info@i4strategies.com</Text> or 
              <Text strong> 212-909-9580</Text></li>
            </ol>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Will I receive a receipt for my payment?
              </span>
            } 
            key="6"
          >
            <Paragraph>
              Yes, you should receive a payment confirmation email after your transaction is successfully 
              processed. This email serves as your receipt and includes:
            </Paragraph>
            <ul>
              <li>Transaction ID</li>
              <li>Payment amount</li>
              <li>Date and time of payment</li>
              <li>Payment method used</li>
            </ul>
            <Paragraph>
              If you don't receive a confirmation email, check your spam folder or contact support 
              to request a copy of your receipt.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                What is the annual recertification fee?
              </span>
            } 
            key="7"
          >
            <Paragraph>
              The annual recertification fee of $750.00 + 2.9% transaction fee is required to maintain 
              your pre-qualified status. This fee covers:
            </Paragraph>
            <ul>
              <li>Annual review and update of your vendor profile</li>
              <li>Continued access to the pre-qualification system</li>
              <li>Ongoing evaluation and maintenance of your qualification status</li>
            </ul>
            <Paragraph>
              You'll be notified when your recertification is due, typically one year from your initial 
              qualification date.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Can I get a refund if I don't complete the PQQ process?
              </span>
            } 
            key="8"
          >
            <Paragraph>
              Refund policies are handled on a case-by-case basis. Generally, once payment has been 
              processed and your PQQ evaluation has begun, refunds are not available. However, if you 
              encounter technical issues or have questions about refund eligibility, please contact 
              support at <Text strong>info@i4strategies.com</Text> or <Text strong>212-909-9580</Text> 
              to discuss your specific situation.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                I'm seeing a "400 Bad Request" error when trying to pay. What should I do?
              </span>
            } 
            key="9"
          >
            <Paragraph>
              A 400 Bad Request error typically indicates an issue with the payment request. Try the following:
            </Paragraph>
            <ol>
              <li>Refresh the page and try again</li>
              <li>Clear your browser cache and cookies</li>
              <li>Ensure your browser is up to date</li>
              <li>Try using a different browser</li>
              <li>Check that your internet connection is stable</li>
              <li>Verify that your profile is 100% complete before attempting payment</li>
            </ol>
            <Paragraph>
              If the error persists, contact support with the exact error message you're seeing, and 
              we'll help troubleshoot the issue.
            </Paragraph>
          </Panel>
        </Collapse>

        <Divider />

        <Title level={4}>Payment Process Steps</Title>
        <ol style={{ paddingLeft: '20px' }}>
          <li>
            <Text strong>Complete Your Profile:</Text> Ensure your i4 Vendor Profile is 100% complete.
          </li>
          <li>
            <Text strong>Navigate to Payment:</Text> Click on "Payment" or "Get Certified" from your Dashboard.
          </li>
          <li>
            <Text strong>Review Amount:</Text> Verify the payment amount ($1,250.00 for initial PQQ).
          </li>
          <li>
            <Text strong>Enter Payment Information:</Text> Provide your credit or debit card details securely.
          </li>
          <li>
            <Text strong>Submit Payment:</Text> Review and confirm your payment.
          </li>
          <li>
            <Text strong>Wait for Confirmation:</Text> You'll receive a confirmation email once payment is processed.
          </li>
          <li>
            <Text strong>Proceed to PQQ Submission:</Text> Once payment is confirmed, you can certify and submit your PQQ.
          </li>
        </ol>

        <Divider />

        <Title level={4}>Security & Privacy</Title>
        <Paragraph>
          <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
          All payments are processed through a secure, PCI-compliant payment gateway.
        </Paragraph>
        <Paragraph>
          <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
          Your payment information is encrypted and never stored on our servers.
        </Paragraph>
        <Paragraph>
          <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
          We use industry-standard security measures to protect your financial data.
        </Paragraph>

        <div style={{ marginTop: '32px', padding: '16px', backgroundColor: '#f0f2f5', borderRadius: '4px' }}>
          <Paragraph style={{ marginBottom: 0 }}>
            <Text strong>Need assistance with payment?</Text> Contact our support team at 
            <Text strong> info@i4strategies.com</Text> or <Text strong>212-909-9580</Text> for help 
            with payment issues or questions.
          </Paragraph>
        </div>
      </Col>
    </Row>
  );
};

export default Payment;
